import apiClient from '../utils/apiClient';

/**
 * Task API Services
 * Quản lý task template và shift task
 */

export const taskAPI = {
  /**
   * Lấy danh sách task template với phân trang và lọc
   * @param {Object} params - Tham số truy vấn
   * @param {string} params.search - Từ khóa tìm kiếm
   * @param {string} params.category - Danh mục task
   * @param {string} params.branch_id - ID chi nhánh
   * @param {boolean} params.is_active - Lọc theo trạng thái active
   * @param {number} params.page - Trang hiện tại
   * @param {number} params.limit - Số lượng mỗi trang
   * @returns {Promise<Object>} Danh sách task và metadata phân trang
   */
  getTasks: async (params = {}) => {
    const { search = '', category = '', branch_id = '', is_active, task_type, is_urgent, page = 1, limit = 10 } = params;

    return await apiClient.get({
      url: '/tasks',
      params: { search, category, branch_id, is_active, task_type, is_urgent, page, limit },
    });
  },

  /**
   * Lấy thông tin chi tiết task theo ID
   * @param {string} taskId - ID task
   * @returns {Promise<Object>} Thông tin chi tiết task
   */
  getTaskById: async (taskId) => {
    return await apiClient.get({
      url: `/tasks/${taskId}`,
    });
  },

  /**
   * Tạo task template mới (MANAGER|ADMIN)
   * @param {Object} taskData - Dữ liệu task
   * @param {string} taskData.name - Tên task
   * @param {string} taskData.category - Danh mục task
   * @param {number} taskData.reward_point - Điểm thưởng
   * @param {number} taskData.penalty_point - Điểm phạt
   * @param {boolean} taskData.is_repeatable - Có thể lặp lại
   * @param {boolean} taskData.requires_review - Cần review
   * @param {string} taskData.branch_id - ID chi nhánh (optional)
   * @returns {Promise<Object>} Thông tin task vừa tạo
   */
  createTask: async (taskData) => {
    return await apiClient.post({
      url: '/tasks',
      data: taskData,
    });
  },

  /**
   * Cập nhật task template
   * @param {string} taskId - ID task
   * @param {Object} updateData - Dữ liệu cập nhật
   * @returns {Promise<Object>} Thông tin task sau khi cập nhật
   */
  updateTask: async (taskId, updateData) => {
    return await apiClient.patch({
      url: `/tasks/${taskId}`,
      data: updateData,
    });
  },

  /**
   * Xóa task template (soft disable)
   * @param {string} taskId - ID task
   * @returns {Promise<Object>} Kết quả xóa
   */
  deleteTask: async (taskId) => {
    return await apiClient.delete({
      url: `/tasks/${taskId}`,
    });
  },

  /**
   * Lấy danh sách shift task
   * @param {Object} params - Tham số truy vấn
   * @param {string} params.shift_id - ID ca làm việc
   * @param {string} params.employee_id - ID nhân viên
   * @param {string} params.status - Trạng thái task (PENDING, COMPLETED, NEED_REVIEW)
   * @param {string} params.from_date - Ngày bắt đầu (YYYY-MM-DD)
   * @param {string} params.to_date - Ngày kết thúc (YYYY-MM-DD)
   * @param {string} params.branch_id - ID chi nhánh
   * @param {number} params.page - Trang hiện tại
   * @param {number} params.limit - Số lượng mỗi trang
   * @returns {Promise<Object>} Danh sách shift task
   */
  getShiftTasks: async (params = {}) => {
    const {
      shift_id = '',
      employee_id = '',
      status = '',
      from_date = '',
      to_date = '',
      branch_id = '',
      page = 1,
      limit = 1000,
    } = params;
    return await apiClient.get({
      url: '/shift-tasks',
      params: { shift_id, employee_id, status, from_date, to_date, branch_id, page, limit },
    });
  },

  /**
   * Lấy thông tin chi tiết shift task theo ID
   * @param {string} shiftTaskId - ID shift task
   * @returns {Promise<Object>} Thông tin chi tiết shift task
   */
  getShiftTaskById: async (shiftTaskId) => {
    return await apiClient.get({
      url: `/shift-tasks/${shiftTaskId}`,
    });
  },

  /**
   * Hoàn thành shift task
   * @param {string} shiftTaskId - ID shift task
   * @param {Object} completionData - Dữ liệu hoàn thành
   * @param {Array} completionData.evidence_urls - URLs bằng chứng (optional)
   * @param {Object} completionData.checklist_completed - Checklist hoàn thành (optional)
   * @returns {Promise<Object>} Thông tin shift task sau khi hoàn thành
   */
  completeShiftTask: async (shiftTaskId, completionData = {}) => {
    return await apiClient.patch({
      url: `/shift-tasks/${shiftTaskId}/complete`,
      data: completionData,
    });
  },

  /**
   * Review shift task (MANAGER|ADMIN|STAFF có quyền)
   * @param {string} shiftTaskId - ID shift task
   * @param {Object} reviewData - Dữ liệu review
   * @param {'APPROVED'|'REJECTED_AND_FIXED'} reviewData.review_result - Kết quả review
   * @param {string} [reviewData.review_notes] - Ghi chú review (optional)
   * @param {string[]} [reviewData.review_evidence_urls] - Bằng chứng (bắt buộc nếu REJECTED_AND_FIXED)
   * @returns {Promise<Object>} Thông tin shift task sau khi review
   */
  reviewShiftTask: async (shiftTaskId, reviewData) => {
    return await apiClient.post({
      url: `/shift-tasks/${shiftTaskId}/review`,
      data: reviewData,
    });
  },

  /**
   * Upload bằng chứng cho shift task
   * @param {string} shiftTaskId - ID shift task
   * @param {FormData} formData - Form data chứa file
   * @returns {Promise<Object>} URLs của file đã upload
   */
  uploadEvidence: async (shiftTaskId, formData) => {
    return await apiClient.post({
      url: `/shift-tasks/${shiftTaskId}/evidence`,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * Lấy danh sách task theo danh mục
   * @param {string} category - Danh mục task
   * @param {Object} params - Tham số truy vấn
   * @returns {Promise<Object>} Danh sách task theo danh mục
   */
  getTasksByCategory: async (category, params = {}) => {
    const { branch_id = '', is_active = true, page = 1, limit = 10 } = params;
    return await apiClient.get({
      url: '/tasks',
      params: { category, branch_id, is_active, page, limit },
    });
  },

  /**
   * #200056 — gợi ý các tiêu chí đã từng dùng cho ảnh/video mẫu, để chọn lại thay vì gõ tay.
   * Ghép từ tiêu chí của mọi task/ yêu cầu task đã lưu, toàn hệ thống. Chỉ đọc.
   * Trả về `{ data: Array<{ name, used_count }> }`.
   * @param {Object} params
   * @param {string} [params.q] - Từ khoá lọc (bỏ dấu, không phân biệt hoa thường)
   * @param {number} [params.limit]
   */
  getCriteriaSuggestions: async (params = {}) => {
    const { q = '', limit = 100 } = params;
    return await apiClient.get({
      url: '/task-center/criteria-suggestions',
      params: { q, limit },
    });
  },

  /**
   * #200056 — ảnh/video mẫu đã dùng ở task trước (cùng chi nhánh), kèm tiêu chí của ảnh.
   * Có phân trang + tìm kiếm vì danh sách phình to theo thời gian. Chỉ đọc.
   * Trả về `{ data, page, limit, total, has_more }`.
   * @param {Object} params
   * @param {string} params.branch_id - Chi nhánh cần lấy ảnh mẫu
   * @param {string} [params.q]
   * @param {number} [params.page]
   * @param {number} [params.limit]
   */
  getSampleEvidenceSuggestions: async (params = {}) => {
    const { branch_id = '', q = '', page = 1, limit = 12 } = params;
    return await apiClient.get({
      url: '/task-center/sample-evidence-suggestions',
      params: { branch_id, q, page, limit },
    });
  },

  /**
   * #181106 — "kho ảnh": ảnh mẫu đã dùng trước + ảnh NHÂN VIÊN đã tải lên làm bằng chứng (cùng
   * chi nhánh). Cùng phân trang + tìm kiếm như danh sách ảnh mẫu. Chỉ đọc.
   * Trả về `{ data, page, limit, total, has_more }`; mỗi phần tử có `source` = 'sample' | 'employee'.
   * @param {Object} params
   * @param {string} params.branch_id
   * @param {string} [params.task_id] #205306 — chỉ lấy ảnh NHÂN VIÊN của đúng việc này
   * @param {string} [params.q]
   * @param {number} [params.page]
   * @param {number} [params.limit]
   */
  getEvidenceLibrary: async (params = {}) => {
    const { branch_id = '', task_id = '', q = '', page = 1, limit = 12 } = params;
    return await apiClient.get({
      url: '/task-center/evidence-library',
      params: { branch_id, task_id, q, page, limit },
    });
  },

  /**
   * Lấy thống kê task
   * @param {Object} params - Tham số truy vấn
   * @param {string} params.branch_id - ID chi nhánh
   * @param {string} params.month - Tháng (YYYY-MM)
   * @returns {Promise<Object>} Thống kê task
   */
  getTaskStats: async (params = {}) => {
    const { branch_id = '', month = '' } = params;
    return await apiClient.get({
      url: '/tasks/stats',
      params: { branch_id, month },
    });
  },

  /**
   * Lấy danh sách shift task cần review
   * @param {Object} params - Tham số truy vấn
   * @param {string} params.branch_id - ID chi nhánh
   * @param {string} params.status - Trạng thái (WAITING_PEER_REVIEW)
   * @param {number} params.page - Trang hiện tại
   * @param {number} params.limit - Số lượng mỗi trang
   * @returns {Promise<Object>} Danh sách shift task cần review
   */
  getTasksNeedReview: async (params = {}) => {
    const { branch_id = '', status = 'WAITING_PEER_REVIEW', page = 1, limit = 10 } = params;
    return await apiClient.get({
      url: '/shift-tasks/for-review',
      params: { branch_id, status, page, limit },
    });
  },

  /**
   * Lấy danh sách shift task chờ admin duyệt kết quả review
   * Tham chiếu: API_TASK_CROSS_REVIEW.md - 3.5.1
   * @param {Object} params
   * @param {string} params.branch_id - ID chi nhánh
   * @param {number} params.page - Trang hiện tại
   * @param {number} params.limit - Số lượng mỗi trang
   * @returns {Promise<Object>} Danh sách shift task chờ admin duyệt
   */
  getAdminReviewPending: async (params = {}) => {
    const { branch_id = '', page = 1, limit = 20 } = params;
    return await apiClient.get({
      url: '/shift-tasks/admin-review-pending',
      params: { branch_id, page, limit },
    });
  },

  /**
   * Admin phân xử tranh chấp cho shift task đã được review
   * Tham chiếu: API_TASK_CROSS_REVIEW.md - 3.5.2
   * @param {string} shiftTaskId - ID shift task
   * @param {Object} reviewData - Dữ liệu phân xử của admin
   * @param {'APPROVE_REVIEWER'|'REJECT_REVIEWER'|'CUSTOM'} reviewData.decision - Quyết định phân xử
   * @param {string} [reviewData.admin_notes] - Ghi chú của admin
   * @param {number} [reviewData.assignee_points_delta] - Điểm cộng/trừ cho người làm (chỉ dùng khi CUSTOM)
   * @param {number} [reviewData.reviewer_points_delta] - Điểm cộng/trừ cho reviewer (chỉ dùng khi CUSTOM)
   * @returns {Promise<Object>} Thông tin shift task sau khi admin phân xử
   */
  adminReviewShiftTask: async (shiftTaskId, reviewData) => {
    return await apiClient.post({
      url: `/shift-tasks/${shiftTaskId}/admin-review`,
      data: reviewData,
    });
  },

  /**
   * #171726 — Quản lý yêu cầu nhân viên LÀM LẠI/CHỤP LẠI một task đã hoàn thành.
   * Đưa task về PENDING để nhân viên nộp lại bằng chứng mới; máy chủ lưu lý do và báo nhân viên.
   * @param {string} shiftTaskId - ID shift task
   * @param {{reason: string}} data - Lý do yêu cầu làm lại (quản lý ghi)
   */
  requestRedoShiftTask: async (shiftTaskId, data) => {
    return await apiClient.post({
      url: `/shift-tasks/${shiftTaskId}/request-redo`,
      data,
    });
  },

  /**
   * Lấy danh sách shift task cho QC review
   * Tham chiếu: QC_SESSION_API_V2.md - 5.1
   * @param {Object} params - Tham số truy vấn
   * @param {string} params.branch_id - ID chi nhánh
   * @param {string} params.start_date - Ngày bắt đầu (YYYY-MM-DD)
   * @param {string} params.end_date - Ngày kết thúc (YYYY-MM-DD)
   * @param {'true'|'false'} params.is_qc_reviewed - Đã review hay chưa
   * @param {string} params.session_id - ID QC session
   * @param {number} params.page - Trang hiện tại
   * @param {number} params.limit - Số lượng mỗi trang
   * @returns {Promise<Object>} Danh sách shift task cho QC review
   */
  getQcTasksForReview: async (params = {}) => {
    const {
      branch_id = '',
      start_date = '',
      end_date = '',
      is_qc_reviewed = '',
      session_id = '',
      page = 1,
      limit = 10,
    } = params;
    return await apiClient.get({
      url: '/shift-tasks/qc/for-review',
      params: { branch_id, start_date, end_date, is_qc_reviewed, session_id, page, limit },
    });
  },

  /**
   * Admin QC Overview – lấy tổng quan shift task theo QC
   * GET /shift-tasks/admin/qc-overview
   * @param {Object} params - Tham số truy vấn
   * @param {string} [params.qc_status] - PENDING | IN_REVIEW | PASSED | FAILED
   * @param {string} [params.qc_type] - IMAGE | CAMERA | FORM_DOCS
   * @param {string} [params.branch_id] - ID chi nhánh
   * @param {string} [params.start_date] - YYYY-MM-DD
   * @param {string} [params.end_date] - YYYY-MM-DD
   * @param {string} [params.session_id] - ID QC session
   * @param {number} [params.page] - Trang
   * @param {number} [params.limit] - Số bản ghi mỗi trang
   * @returns {Promise<Object>} { stats, data, meta }
   */
  getAdminQcOverview: async (params = {}) => {
    const {
      qc_status = '',
      qc_type = '',
      branch_id = '',
      start_date = '',
      end_date = '',
      session_id = '',
      page = 1,
      limit = 10,
    } = params;
    return await apiClient.get({
      url: '/shift-tasks/admin/qc-overview',
      params: { qc_status, qc_type, branch_id, start_date, end_date, session_id, page, limit },
    });
  },
};
